// A3/A5 — report a UI exception to POST /client-error (authenticated route, so
// the resulting deduped support ticket links to the signed-in user). Best-effort
// and completely silent: reporting must never itself throw or surface to the user.
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL as string;

// Dedup within the tab so a render loop doesn't hammer the endpoint (the backend
// also dedups per (signature,user,day), this just cuts network noise).
const seen = new Set<string>();

export async function reportClientError(input: {
  message: string;
  stack?: string;
  componentStack?: string;
  route?: string;
  status?: number;
}): Promise<void> {
  try {
    const key = `${input.message}|${input.route ?? ''}`.slice(0, 200);
    if (seen.has(key)) return;
    seen.add(key);

    const session = await fetchAuthSession().catch(() => null);
    const token = session?.tokens?.idToken?.toString();
    if (!token) return; // only report for authenticated sessions (endpoint is authed)

    await fetch(`${API_URL}/client-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        app: 'frontend',
        message: input.message?.slice(0, 300),
        stack: input.stack?.slice(0, 4000),
        componentStack: input.componentStack?.slice(0, 2000),
        route: input.route ?? (typeof location !== 'undefined' ? location.pathname : ''),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        // Pass the HTTP status so the backend can gate: only 5xx become tickets.
        ...(typeof input.status === 'number' ? { status: input.status } : {})
      })
    });
  } catch {
    /* swallow — reporting must never break the app */
  }
}

// Report a HANDLED React Query error (mutation/query) as an auto-ticket, but
// ONLY when it signals a genuine SERVER or NETWORK defect — never a 4xx.
//
// Policy (aligned with the server's errorToApiGateway, which also skips <500):
// a 4xx is a client/input problem the UI already surfaces inline (a plan cap, an
// expected state guard, or a bad field the user can correct) — not a server
// defect, so it must not auto-file an exception ticket. Previously 400s were
// ticketed as "form-validation gaps", which turned every user typo (bad hex,
// invalid URL/email, missing field) and every plan-cap into support noise — the
// bulk of the exception backlog. Form-validation gaps are better found via UX
// review than a per-user ticket stream. We still ticket:
//   • 5xx — a real server fault, and
//   • network failures (no status) — fetch/connectivity errors.
// reportClientError + the backend both dedupe, and the backend now re-checks the
// status as a backstop.
export function reportQueryFault(error: unknown): void {
  const status = (error as { status?: number } | null)?.status;
  const msg = error instanceof Error ? error.message : String(error);
  let report: boolean;
  if (typeof status === 'number') {
    report = status >= 500; // only genuine server faults
  } else {
    report = /failed to fetch|networkerror|load failed|network request failed/i.test(msg);
  }
  if (!report) return;
  void reportClientError({
    message: msg,
    stack: error instanceof Error ? error.stack : undefined,
    ...(typeof status === 'number' ? { status } : {})
  });
}

// Global handlers for errors that escape React (async, event handlers, promises).
let installed = false;
export function installGlobalErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => {
    void reportClientError({ message: e.message || 'window.error', stack: e.error?.stack });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    void reportClientError({
      message: (r?.message || String(r) || 'unhandledrejection').slice(0, 300),
      stack: r?.stack
    });
  });
}
